import { useMemo, useState } from 'react';
import {
  Building2,
  ChevronsUpDown,
  Globe,
  Loader2,
  Plus,
  Search,
  SearchX,
  User,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { cn } from '@/lib/utils';
import { useGetCompanies } from '../hooks/use-companies';
import { toWebsiteLabel } from '../lib/website-url';
import { CompanyFormDialog } from './company-form-dialog';
import type { Company, CompanyDraft, CompanyScope } from '../types/companies.types';

const SCOPE_BADGE: Record<CompanyScope, { labelKey: string; icon: typeof User }> = {
  user: { labelKey: 'ORGCOMPANIES_BADGE_PERSONAL', icon: User },
  organization: { labelKey: 'ORGCOMPANIES_BADGE_ORG', icon: Building2 },
};

interface CompanyPickerProps {
  onSelect: (company: Company) => void;
  /** Action-button trigger text (e.g. "Use a company"). Ignored when `value` is given. */
  label?: string;
  /**
   * Select mode: pass the currently-selected company name (empty string = nothing picked).
   * The trigger then renders as a dropdown — value or muted `placeholder`, plus a chevron.
   */
  value?: string;
  placeholder?: string;
  /** Id for the trigger so a <Label htmlFor> can point at it. */
  triggerId?: string;
  /**
   * When set, the create CTAs call this instead of opening the picker's own dialog —
   * required for callers already inside a dialog, which must render the company form
   * in place rather than stacking a second dialog. Receives the typed search text.
   */
  onRequestCreate?: (prefillName: string) => void;
  /** When set (and something is selected), the list offers a "Clear selection" row. */
  onClear?: () => void;
  allowCreate?: boolean;
  canShareWithOrg?: boolean;
  createDefaults?: CompanyDraft;
  align?: 'start' | 'center' | 'end';
  triggerClassName?: string;
}

export function CompanyPicker({
  onSelect,
  label,
  value,
  placeholder,
  triggerId,
  onRequestCreate,
  onClear,
  allowCreate = true,
  canShareWithOrg = true,
  createDefaults,
  align = 'start',
  triggerClassName,
}: CompanyPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { data: companies, isLoading } = useGetCompanies(open || createOpen);

  /** `value` present (even as '') means "render as a form field", not an action button. */
  const isSelectMode = value !== undefined;
  const trimmedQuery = query.trim();
  // Nothing saved at all vs. nothing matching the search — different messages, different CTAs.
  const hasNoCompanies = (companies?.length ?? 0) === 0;

  const filtered = useMemo(() => {
    const list = companies ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      [c.name, c.email, c.website, c.address].some((f) => f && f.toLowerCase().includes(q))
    );
  }, [companies, query]);

  const pick = (c: Company) => {
    onSelect(c);
    setOpen(false);
    setCreateOpen(false);
  };

  // Close the popover before opening the dialog so their focus traps don't fight.
  // When the caller owns creation (it's already in a dialog), hand off instead.
  const openCreate = () => {
    setOpen(false);
    if (onRequestCreate) onRequestCreate(trimmedQuery);
    else setCreateOpen(true);
  };

  const clearSelection = () => {
    onClear?.();
    setOpen(false);
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setQuery('');
        }}
      >
        <PopoverTrigger asChild>
          {isSelectMode ? (
            // Dropdown-style trigger: selected name, or muted placeholder, plus a chevron.
            <Button
              type="button"
              id={triggerId}
              variant="outline"
              size="sm"
              className={cn('w-full justify-between gap-1.5 font-normal', triggerClassName)}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 shrink-0 text-selise-blue" />
                <span className={cn('truncate', !value && 'text-globe-grey')}>
                  {value || placeholder || t('ORGCOMPANIES_PICKER_LABEL')}
                </span>
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
            </Button>
          ) : (
            <Button
              type="button"
              id={triggerId}
              variant="outline"
              size="sm"
              className={cn('gap-1.5', triggerClassName)}
            >
              <Building2 className="h-3.5 w-3.5 shrink-0 text-selise-blue" />
              <span className="truncate">{label || t('ORGCOMPANIES_PICKER_LABEL')}</span>
            </Button>
          )}
        </PopoverTrigger>
        {/*
          z-index must beat DialogContent (z-9999): this picker is used inside the contact
          form dialog, and PopoverContent's ui-kit default (z-500) renders it *behind* the
          dialog overlay, which looks exactly like the popover failing to open.
        */}
        <PopoverContent
          align={align}
          side="bottom"
          sideOffset={6}
          // Keeps the panel inside the viewport (flipping above the trigger if needed)
          // instead of opening off-screen below a tall dialog.
          collisionPadding={12}
          className="w-80 p-0 z-10000"
        >
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-4 w-4 text-globe-grey shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('ORGCOMPANIES_PICKER_SEARCH')}
              aria-label={t('ORGCOMPANIES_PICKER_SEARCH')}
              className="w-full bg-transparent text-sm outline-none placeholder:text-globe-grey"
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-globe-grey">
                <Loader2 className="h-4 w-4 animate-spin" /> {t('ORGCOMPANIES_PICKER_LOADING')}
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                  {hasNoCompanies ? (
                    <Building2 className="h-5 w-5 text-globe-grey" />
                  ) : (
                    <SearchX className="h-5 w-5 text-globe-grey" />
                  )}
                </div>
                <p className="text-sm font-medium text-[hsl(var(--fg))]">
                  {hasNoCompanies
                    ? t('ORGCOMPANIES_PICKER_EMPTY')
                    : t('ORGCOMPANIES_PICKER_NO_MATCH')}
                </p>
                <p className="text-xs text-globe-grey">
                  {hasNoCompanies
                    ? t('ORGCOMPANIES_PICKER_EMPTY_HELP')
                    : t('ORGCOMPANIES_PICKER_NO_MATCH_HELP')}
                </p>
                {allowCreate && (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="mt-1"
                    onClick={openCreate}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {hasNoCompanies
                      ? t('ORGCOMPANIES_PICKER_EMPTY_CTA')
                      : trimmedQuery
                        ? t('ORGCOMPANIES_PICKER_CREATE_NAMED', { name: trimmedQuery })
                        : t('ORGCOMPANIES_PICKER_CREATE')}
                  </Button>
                )}
              </div>
            ) : (
              <ul className="py-1">
                {/* Unselect — only meaningful once something is picked. */}
                {onClear && !!value && (
                  <li>
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="flex w-full items-center gap-2.5 border-b border-border px-3 py-2 text-left text-sm text-globe-grey hover:bg-muted transition-colors"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted shrink-0">
                        <X className="h-4 w-4" />
                      </div>
                      {t('ORGCOMPANIES_PICKER_CLEAR')}
                    </button>
                  </li>
                )}
                {filtered.map((c) => {
                  const badge = SCOPE_BADGE[c.scope];
                  const BadgeIcon = badge.icon;
                  const secondary = [toWebsiteLabel(c.website), c.email]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => pick(c)}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-muted transition-colors"
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-selise-blue/10 text-selise-blue shrink-0">
                          <BadgeIcon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-[hsl(var(--fg))] truncate">
                              {c.name}
                            </span>
                            <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-globe-grey">
                              {t(badge.labelKey)}
                            </span>
                          </div>
                          {secondary && (
                            <p className="flex items-center gap-1 text-[11px] text-globe-grey truncate">
                              <Globe className="h-3 w-3 shrink-0" />
                              {secondary}
                            </p>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* The empty state carries its own CTA, so the footer would just duplicate it. */}
          {allowCreate && filtered.length > 0 && (
            <button
              type="button"
              onClick={openCreate}
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2.5 text-sm font-medium text-selise-blue hover:bg-muted transition-colors"
            >
              <Plus className="h-4 w-4" />
              {trimmedQuery
                ? t('ORGCOMPANIES_PICKER_CREATE_NAMED', { name: trimmedQuery })
                : t('ORGCOMPANIES_PICKER_CREATE')}
            </button>
          )}
        </PopoverContent>
      </Popover>

      {/* Not rendered when the caller owns creation — that's what avoids a stacked dialog. */}
      {allowCreate && !onRequestCreate && (
        <CompanyFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          canShareWithOrg={canShareWithOrg}
          defaults={{ ...createDefaults, name: createDefaults?.name || query.trim() || '' }}
          onCreated={pick}
        />
      )}
    </>
  );
}
